import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const connectorsFetch = createServiceFetch(config.api.baseUrl)

import type {
  EvalDatasetJoinPreviewResponse,
  EvalDatasetJoinMaterializeResponse,
  EvalSessionBenchmarkJoinPreviewResponse,
} from './agentEvals'
import { projectsApi } from './projects'
import type {
  ConnectorConnection,
  ServiceConnectionTypeSpec,
  ConnectorCatalogOption,
  ConnectorConnectionCreate,
  ConnectorTestResponse,
  IntrospectionResponse,
  TableInfo,
  RegisterTablesResponse,
  TableDataResponse,
  VectorSearchResponse,
  RegisteredTable,
  StorageIntrospectionResponse,
  StorageIntrospectionDepth,
  RegisterStorageFoldersResponse,
  RegisteredStorageFolder,
  StorageFilesResponse,
  StorageFilePreviewResponse,
  ExternalAgentStatusType,
} from './types'

// Connector Connections API
export const connectorsApi = {
  listTypes: () =>
    request<Record<string, { display_name: string; category: string }>>('/connectors/types'),

  listServiceTypes: () => request<ServiceConnectionTypeSpec[]>('/connectors/service-types'),

  /** Unpaginated list for dropdowns/selectors. Returns all connectors matching filters. */
  listAll: (params?: {
    project_id?: string
    connector_type?: string
    connector_category?: string
    exclude_vector_db?: boolean
    exclude_graph_db?: boolean
    workflow_database_node?: string
    /** Filter to connectors created by this user id. */
    created_by?: string
    status?: ExternalAgentStatusType
    has_registered_storage_folders?: boolean
    /** Only connections that have an approved table on this project. */
    approved_tables_for_project?: string
    /** With project_id, keep only connections linked to that project. */
    project_scoped?: boolean
    signal?: AbortSignal
    tenantId?: string
  }) => {
    const { signal, tenantId, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: ConnectorConnection[]; total: number }>(`/connectors/list-all${query}`, {
      signal,
      tenantId,
    })
  },

  /**
   * Minimal connectors for dropdowns (id, display_name, connector_type).
   * Same RBAC as list-all. When project_id is set, only connections linked to that
   * project are returned. Omit project_id for tenant-wide catalogs.
   * Use scope tabular | graph | vector.
   */
  listCatalogOptions: (
    params?: {
      project_id?: string
      scope?: 'tabular' | 'graph' | 'vector'
      /** Narrow the connector instance list to this type (matches GET /connectors/catalog-options). */
      connector_type?: string
      signal?: AbortSignal
    }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: ConnectorCatalogOption[]; total: number }>(
      `/connectors/catalog-options${query}`,
      { signal }
    )
  },

  /**
   * Paginated variant of /connectors/list-all used by the Split List Explorer
   * (Data page connector pane).
   *
   * TODO(split-list-backend): Backend must add `page`, `page_size`, `search`,
   * `order_by`, `order_desc` query params to /api/connectors/list-all while
   * preserving the existing unpaginated behavior when these params are absent.
   * See frontend/src/components/SplitListExplorer/BACKEND_SPEC.md (section 3).
   */
  listAllPaged: (params?: {
    project_id?: string
    connector_type?: string
    connector_category?: string
    exclude_vector_db?: boolean
    exclude_graph_db?: boolean
    workflow_database_node?: string
    /** Filter to connectors created by this user id. */
    created_by?: string
    status?: ExternalAgentStatusType
    has_registered_storage_folders?: boolean
    page?: number
    page_size?: number
    search?: string
    order_by?: string
    order_desc?: boolean
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const orderByFieldMap: Record<string, string> = {
      name: 'display_name',
      type: 'connector_type',
      created: 'created_at',
      updated: 'updated_at',
    }
    const normalized = {
      ...queryParams,
      order_by: queryParams.order_by
        ? (orderByFieldMap[queryParams.order_by] ?? queryParams.order_by)
        : queryParams.order_by,
    }
    const query = buildQueryString(normalized)
    return request<{ items: ConnectorConnection[]; total: number }>(
      `/connectors/list-all${query}`,
      { signal }
    )
  },

  /**
   * Distinct schemas (database+schema tuples) registered under a single
   * connector. Powers pane 2 of the Knowledge / Graph / Vector DB split view.
   *
   * TODO(split-list-backend): Implement
   * GET /api/connectors/{connector_id}/registered-table-schemas
   * See frontend/src/components/SplitListExplorer/BACKEND_SPEC.md (section 1).
   */
  listConnectorTableSchemas: (
    connectorId: string,
    params?: {
      page?: number
      page_size?: number
      search?: string
      project_id?: string
      exclude_vector_db?: boolean
      exclude_graph_db?: boolean
      graph_only?: boolean
      connector_category?: string
      knowledge_type?: 'data' | 'benchmarks'
      order_by?: string
      order_desc?: boolean
    },
    options?: { signal?: AbortSignal }
  ) => {
    const orderByFieldMap: Record<string, string> = {
      database: 'database',
      schema: 'schema',
      tables: 'table_count',
      updated: 'last_updated_at',
    }
    const normalized = params
      ? {
          ...params,
          order_by: params.order_by
            ? (orderByFieldMap[params.order_by] ?? params.order_by)
            : params.order_by,
        }
      : undefined
    const query = buildQueryString(normalized || {})
    return request<{
      items: Array<{
        connector_connection_id: string
        database: string | null
        schema: string | null
        table_count: number
        last_updated_at?: number | null
      }>
      total: number
    }>(`/connectors/${connectorId}/registered-table-schemas${query}`, {
      signal: options?.signal,
    })
  },

  /**
   * Distinct buckets registered under a single storage connector.
   * Powers pane 2 of the Storage tab split view.
   *
   * TODO(split-list-backend): Implement
   * GET /api/connectors/{connector_id}/registered-storage-buckets
   * See frontend/src/components/SplitListExplorer/BACKEND_SPEC.md (section 2).
   */
  listConnectorStorageBuckets: (
    connectorId: string,
    params?: {
      page?: number
      page_size?: number
      search?: string
      project_id?: string
      order_by?: string
      order_desc?: boolean
    },
    options?: { signal?: AbortSignal }
  ) => {
    const orderByFieldMap: Record<string, string> = {
      bucket: 'bucket_name',
      folders: 'folder_count',
      updated: 'last_updated_at',
    }
    const normalized = params
      ? {
          ...params,
          order_by: params.order_by
            ? (orderByFieldMap[params.order_by] ?? params.order_by)
            : params.order_by,
        }
      : undefined
    const query = buildQueryString(normalized || {})
    return request<{
      items: Array<{
        connector_connection_id: string
        bucket_name: string
        folder_count: number
        last_updated_at?: number | null
      }>
      total: number
    }>(`/connectors/${connectorId}/registered-storage-buckets${query}`, {
      signal: options?.signal,
    })
  },

  list: (params?: {
    search?: string
    page?: number
    page_size?: number
    project_id?: string
    connector_type?: string
    connector_category?: string
    exclude_vector_db?: boolean
    /** Backend filters to connector types allowed for workflow SQL/NoSQL/Graph reader or writer nodes. */
    workflow_database_node?: string
    created_by?: string
    status?: ExternalAgentStatusType
    order_by?: string
    order_desc?: boolean
    is_favourite?: boolean
    scope?: 'all'
    omit_linked_for_project?: string
    only_approved_linked_for_project?: string
    include_project_membership_for?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<{ items: ConnectorConnection[]; total: number }>(`/connectors${query}`, {
      signal,
    })
  },

  create: (data: ConnectorConnectionCreate, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<ConnectorConnection>('/connectors', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  update: (
    id: string,
    data: Partial<ConnectorConnectionCreate>,
    options?: { signal?: AbortSignal; tenantId?: string }
  ) =>
    request<ConnectorConnection>(`/connectors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  delete: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<void>(`/connectors/${id}`, {
      method: 'DELETE',
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  bulkDelete: (connectorIds: string[], options?: { force?: boolean; signal?: AbortSignal; tenantId?: string }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>('/connectors/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ connector_ids: connectorIds }),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  get: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<ConnectorConnection>(`/connectors/${id}`, {
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  testConnection: (
    id: string,
    options?: { signal?: AbortSignal; tenantId?: string; projectId?: string },
  ) =>
    request<ConnectorTestResponse>(`/connectors/${id}/test`, {
      method: 'POST',
      signal: options?.signal,
      tenantId: options?.tenantId,
      headers: options?.projectId
        ? { 'X-Workflow-Project-ID': options.projectId }
        : undefined,
    }),

  // Test a connector configuration without saving it
  testConfig: (data: ConnectorConnectionCreate, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<ConnectorTestResponse>('/connectors/test-config', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  // Get the default storage connector
  getDefaultStorage: (options?: { signal?: AbortSignal }) =>
    request<ConnectorConnection | null>('/connectors/storage/default', {
      signal: options?.signal,
    }),

  // Set a connector as the default for its category (storage, database, or orchestrator)
  setDefault: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<ConnectorConnection>(`/connectors/${id}/set-default`, {
      method: 'POST',
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  // Alias for backward compatibility
  setDefaultStorage: (id: string, options?: { signal?: AbortSignal }) =>
    request<ConnectorConnection>(`/connectors/${id}/set-default`, {
      method: 'POST',
      signal: options?.signal,
    }),

  introspect: (
    id: string,
    params?: {
      database?: string
      schema?: string
      depth?: 'schemas_only' | 'catalog' | 'tables_no_counts' | 'full'
    },
    options?: { signal?: AbortSignal }
  ) =>
    request<IntrospectionResponse>(`/connectors/${id}/introspect`, {
      method: 'POST',
      body: JSON.stringify({ connector_id: id, ...params }),
      signal: options?.signal,
    }),

  /** List graphs/containers for a Cosmos multi-graph connection. */
  listGraphs: (id: string, options?: { signal?: AbortSignal }) =>
    request<{ graphs: string[]; multi_graph_mode: boolean }>(`/connectors/${id}/graphs`, {
      signal: options?.signal,
    }),

  /** Create a graph/container on a Cosmos multi-graph connection. */
  createGraph: (
    id: string,
    body: { graph_name: string; partition_key?: string; throughput?: number },
    options?: { signal?: AbortSignal }
  ) =>
    request<{
      graph: string
      database: string
      partition_key: string
      throughput?: number | null
      container_id?: string | null
    }>(`/connectors/${id}/graphs`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal: options?.signal,
    }),

  registerTables: (
    id: string,
    tables: TableInfo[],
    options?: {
      embeddingModelId?: string
      knowledgeType?: 'data' | 'benchmarks'
      vectorColumnConfig?: { id_column: string; content_column: string; embedding_column: string }
      graphSchema?: { labels: string[]; relationship_types?: Array<Record<string, unknown>> }
      signal?: AbortSignal
    }
  ) =>
    request<RegisterTablesResponse>(`/connectors/${id}/register-tables`, {
      method: 'POST',
      body: JSON.stringify({
        connector_id: id,
        tables,
        embedding_model_id: options?.embeddingModelId ?? undefined,
        knowledge_type: options?.knowledgeType ?? undefined,
        vector_column_config: options?.vectorColumnConfig ?? undefined,
        graph_schema: options?.graphSchema ?? undefined,
      }),
      signal: options?.signal,
    }),

  getRegisteredStorageFolder: (folderId: string, options?: { signal?: AbortSignal }) =>
    request<RegisteredStorageFolder>(`/connectors/registered-storage-folders/${folderId}`, {
      signal: options?.signal,
    }),

  // Get a single registered table by ID
  getRegisteredTable: (tableId: string, options?: { signal?: AbortSignal }) =>
    request<RegisteredTable>(`/connectors/registered-tables/${tableId}`, {
      signal: options?.signal,
    }),

  // Get sibling tables (tables sharing the same connection) for JOIN support
  getSiblingTables: (tableId: string, options?: { signal?: AbortSignal }) =>
    request<RegisteredTable[]>(`/connectors/registered-tables/${tableId}/siblings`, {
      signal: options?.signal,
    }),

  // Update a registered table (e.g., change embedding model, ontology fields)
  updateRegisteredTable: (
    tableId: string,
    data: Partial<RegisteredTable>,
    options?: { signal?: AbortSignal }
  ) =>
    request<RegisteredTable>(`/connectors/registered-tables/${tableId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  listAllRegisteredTables: (
    params?: {
      project_id?: string
      page?: number
      page_size?: number
      search?: string
      connector_type?: string // Filter by connector type (e.g., 'postgres', 'databricks')
      connector_category?: string // Filter by connector category (e.g., 'database', 'vector_db')
      knowledge_type?: 'data' | 'benchmarks' // Filter by knowledge type (benchmarks = evals/simulations)
      /** Filter by benchmark workload type (text2sql, rag, deep_rag, tool_use, qa, other) */
      benchmark_type?: string
      exclude_vector_db?: boolean // Exclude vector DB tables (for Knowledge tab)
      exclude_graph_db?: boolean // Exclude graph DB tables (Neo4j, Neptune, Cosmos) from Knowledge tab
      graph_only?: boolean // Only graph DB registered tables (Graph tab)
      /** Exclude items with approved project_assets for this project */
      omit_linked_for_project?: string
      /** Return only items with approved project_assets for this project */
      only_approved_linked_for_project?: string
      /** Include project_membership_status on each item for this project */
      include_project_membership_for?: string
      connector_connection_id?: string
      database?: string
      schema?: string
      database_is_null?: boolean
      schema_is_null?: boolean
      /** Filter by connector engine (registered connection type, e.g. postgres) */
      table_connector_type?: string
      /** Filter by creator user id */
      table_created_by?: string
      order_by?: string
      order_desc?: boolean
      is_favourite?: boolean
    },
    options?: { signal?: AbortSignal; tenantId?: string }
  ) => {
    // DataTable column keys differ from backend sort field names for this endpoint.
    // Normalize here so all callers can use UI-facing keys consistently.
    const orderByFieldMap: Record<string, string> = {
      name: 'display_name',
      table: 'table_name',
      database: 'database_name',
      creator: 'created_by',
      updated: 'updated_at',
      created: 'created_at',
    }
    const normalizedParams = params
      ? {
        ...params,
        order_by: params.order_by
          ? (orderByFieldMap[params.order_by] ?? params.order_by)
          : params.order_by,
      }
      : undefined

    const query = buildQueryString(normalizedParams || {})
    // Use /all/registered-tables to avoid conflict with /{connector_id}/registered-tables
    return request<{ items: RegisteredTable[]; total: number }>(
      `/connectors/all/registered-tables${query}`,
      {
        signal: options?.signal,
        tenantId: options?.tenantId,
      }
    )
  },

  listConnectorFilterFacets: (
    params?: {
      project_id?: string
      connector_category?: string
      exclude_vector_db?: boolean
      exclude_graph_db?: boolean
      workflow_database_node?: string
      scope?: 'all'
    },
    options?: { signal?: AbortSignal }
  ) => {
    const query = buildQueryString(params || {})
    return request<{
      connector_types: string[]
      creators: { user_id: string; label: string }[]
    }>(`/connectors/filter-facets${query}`, {
      signal: options?.signal,
    })
  },

  listRegisteredTableFilterFacets: (
    params?: {
      project_id?: string
      connector_connection_id?: string
      connector_category?: string
      knowledge_type?: 'data' | 'benchmarks'
      exclude_vector_db?: boolean
      exclude_graph_db?: boolean
      graph_only?: boolean
      omit_linked_for_project?: string
      only_approved_linked_for_project?: string
    },
    options?: { signal?: AbortSignal }
  ) => {
    const query = buildQueryString(params || {})
    return request<{
      connector_types: string[]
      creators: { user_id: string; label: string }[]
    }>(`/connectors/all/registered-tables/filter-facets${query}`, {
      signal: options?.signal,
    })
  },

  listRegisteredTables: (
    id: string,
    params?: {
      page?: number
      page_size?: number
      database?: string
      schema?: string
      table?: string
      search?: string
    },
    options?: { signal?: AbortSignal }
  ) => {
    const query = buildQueryString(params || {})
    return request<{ items: RegisteredTable[]; total: number }>(
      `/connectors/${id}/registered-tables${query}`,
      {
        signal: options?.signal,
      }
    )
  },

  unregisterTable: (tableId: string, options?: { signal?: AbortSignal }) =>
    request<void>(`/connectors/registered-tables/${tableId}`, {
      method: 'DELETE',
      signal: options?.signal,
    }),

  bulkUnregisterTables: (tableIds: string[], options?: { force?: boolean; signal?: AbortSignal }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>(
      '/connectors/registered-tables/bulk-delete',
      {
        method: 'POST',
        body: JSON.stringify({ table_ids: tableIds }),
        signal: options?.signal,
      }
    ),

  /**
   * Unregister all registered tables under one (database, schema) group.
   * Does not DROP the physical schema in the source database.
   */
  deleteRegisteredTableSchema: (
    connectorId: string,
    params: { database: string | null; schema: string | null },
    options?: { force?: boolean; signal?: AbortSignal }
  ) => {
    const query = buildQueryString(options?.force ? { force: true } : {})
    return request<{ deleted: number; skipped: number; errors: string[] }>(
      `/connectors/${connectorId}/registered-table-schemas/delete${query}`,
      {
        method: 'POST',
        body: JSON.stringify({
          database: params.database,
          schema: params.schema,
        }),
        signal: options?.signal,
      }
    )
  },

  // Sync/refresh metadata for a registered table by re-introspecting it from the connector
  syncRegisteredTable: (tableId: string, options?: { signal?: AbortSignal }) =>
    request<{
      success: boolean
      registered_table_id: string
      message: string
      updated_columns?: number
      updated_row_count?: number | null
    }>(`/connectors/registered-tables/${tableId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ registered_table_id: tableId }),
      signal: options?.signal,
    }),

  enqueueMetadataSync: (
    tableId: string,
    options?: { signal?: AbortSignal; force?: boolean }
  ) => {
    const q = new URLSearchParams()
    if (options?.force) q.set('force', 'true')
    const suffix = q.toString() ? `?${q.toString()}` : ''
    return request<{ accepted: boolean; registered_table_id: string }>(
      `/connectors/registered-tables/${tableId}/metadata-sync${suffix}`,
      {
        method: 'POST',
        signal: options?.signal,
      }
    )
  },

  /** Single lightweight poll for introspection fields (queued / in_progress tables). */
  getMetadataSyncTableStatuses: (tableIds: string[], options?: { signal?: AbortSignal }) => {
    const capped = [...new Set(tableIds)].slice(0, 100)
    if (capped.length === 0) {
      return Promise.resolve({
        items: [] as Array<{
          id: string
          metadata_sync_status: string
          metadata_sync_error: string | null
          metadata_last_synced_at: number | null
          metadata_sync_started_at: number | null
        }>,
      })
    }
    const q = new URLSearchParams({ table_ids: capped.join(',') })
    return request<{
      items: Array<{
        id: string
        metadata_sync_status: string
        metadata_sync_error: string | null
        metadata_last_synced_at: number | null
        metadata_sync_started_at: number | null
      }>
    }>(`/connectors/registered-tables/metadata-sync/table-statuses?${q.toString()}`, {
      signal: options?.signal,
    })
  },

  bulkEnqueueMetadataSync: (
    tableIds: string[],
    options?: { signal?: AbortSignal; force?: boolean }
  ) =>
    request<{ bulk_job_id: string }>(`/connectors/registered-tables/metadata-sync/bulk`, {
      method: 'POST',
      body: JSON.stringify({ table_ids: tableIds, force: Boolean(options?.force) }),
      signal: options?.signal,
    }),

  getBulkMetadataSyncJob: (bulkJobId: string, options?: { signal?: AbortSignal }) =>
    request<{
      id: string
      tenant_id: string | null
      status: string
      counts_json: Record<string, number> | null
      started_at: number | null
      completed_at: number | null
      created_at: number | null
      items: Array<{
        id: string
        registered_table_id: string
        status: string
        error_message: string | null
        display_name: string | null
      }>
    }>(`/connectors/registered-tables/metadata-sync/bulk/${bulkJobId}`, {
      signal: options?.signal,
    }),

  retryBulkMetadataSyncFailed: (bulkJobId: string, options?: { signal?: AbortSignal }) =>
    request<{ retried: number }>(
      `/connectors/registered-tables/metadata-sync/bulk/${bulkJobId}/retry-failed`,
      {
        method: 'POST',
        signal: options?.signal,
      }
    ),

  listBulkMetadataSyncJobs: (options?: { limit?: number; signal?: AbortSignal }) => {
    const limit = options?.limit ?? 20
    const q = new URLSearchParams({ limit: String(limit) })
    return request<
      Array<{
        id: string
        tenant_id: string | null
        status: string
        counts_json: Record<string, number> | null
        created_at: number | null
        completed_at: number | null
      }>
    >(`/connectors/registered-tables/metadata-sync/bulk?${q.toString()}`, {
      signal: options?.signal,
    })
  },

  /** Historical metadata sync attempts for a registered table (newest first). */
  listMetadataSyncLogs: (
    tableId: string,
    options?: { limit?: number; signal?: AbortSignal }
  ) => {
    const q = new URLSearchParams()
    if (options?.limit != null) q.set('limit', String(options.limit))
    const suffix = q.toString() ? `?${q.toString()}` : ''
    return request<{
      items: Array<{
        id: string
        registered_table_id: string | null
        registered_storage_folder_id: string | null
        bulk_job_id: string | null
        status: string
        message: string | null
        detail: string | null
        details_json: Record<string, unknown> | null
        trigger: string | null
        created_at: number | null
        completed_at: number | null
      }>
      total: number
    }>(`/connectors/registered-tables/${tableId}/metadata-sync/logs${suffix}`, {
      signal: options?.signal,
    })
  },

  /** Historical storage sync attempts for a registered folder (newest first). */
  listStorageSyncLogs: (
    folderId: string,
    options?: { limit?: number; signal?: AbortSignal }
  ) => {
    const q = new URLSearchParams()
    if (options?.limit != null) q.set('limit', String(options.limit))
    const suffix = q.toString() ? `?${q.toString()}` : ''
    return request<{
      items: Array<{
        id: string
        registered_table_id: string | null
        registered_storage_folder_id: string | null
        bulk_job_id: string | null
        status: string
        message: string | null
        detail: string | null
        details_json: Record<string, unknown> | null
        trigger: string | null
        created_at: number | null
        completed_at: number | null
      }>
      total: number
    }>(`/connectors/registered-storage-folders/${folderId}/sync-logs${suffix}`, {
      signal: options?.signal,
    })
  },

  fetchTableData: (
    tableId: string,
    params: {
      page?: number
      page_size?: number
      search?: string
      order_by?: string
      order_desc?: boolean
      selected_labels?: string[]
      /** Cumulative KG snapshot: entities introduced at or before this commit version. */
      max_commit_version?: number
    },
    options?: { signal?: AbortSignal }
  ) => {
    const body: Record<string, unknown> = {
      registered_table_id: tableId,
      page: params.page || 1,
      page_size: params.page_size || 50,
      order_direction: params.order_desc === false ? 'ASC' : 'DESC',
    }

    // Only include optional fields if they have values
    if (params.search !== undefined && params.search !== null && params.search !== '') {
      body.search = params.search
    }
    // Omit empty / whitespace order_by — BE warns when the column is not on the table
    // (e.g. defaulting to "updated_at" for tables that lack it).
    if (typeof params.order_by === 'string' && params.order_by.trim() !== '') {
      body.order_by = params.order_by.trim()
    }
    if (params.selected_labels && params.selected_labels.length > 0) {
      body.selected_labels = params.selected_labels
    }
    if (
      typeof params.max_commit_version === 'number' &&
      Number.isFinite(params.max_commit_version) &&
      params.max_commit_version >= 1
    ) {
      body.max_commit_version = Math.floor(params.max_commit_version)
    }

    return request<TableDataResponse>(`/connectors/registered-tables/${tableId}/data`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal: options?.signal,
    })
  },

  bulkInsertRegisteredTable: (tableId: string, rows: Array<Record<string, unknown>>) =>
    request<{ rows_affected: number; success: boolean; dropped_keys: string[] }>(
      `/connectors/registered-tables/${tableId}/bulk-insert`,
      { method: 'POST', body: JSON.stringify({ rows }) }
    ),

  truncateRegisteredTable: (tableId: string) =>
    request<{ success: boolean; table: string }>(`/connectors/registered-tables/${tableId}/truncate`, {
      method: 'POST',
    }),

  getGraphSchema: (tableId: string, options?: { signal?: AbortSignal }) =>
    request<{ graph_schema: import('./types').GraphSchemaInfo }>(
      `/connectors/registered-tables/${tableId}/graph-schema`,
      { signal: options?.signal }
    ),

  updateGraphSchema: (
    tableId: string,
    data: { labels?: string[]; relationship_types?: Array<Record<string, unknown>> },
    options?: { signal?: AbortSignal }
  ) =>
    request<{ graph_schema: import('./types').GraphSchemaInfo }>(
      `/connectors/registered-tables/${tableId}/graph-schema`,
      { method: 'PATCH', body: JSON.stringify(data), signal: options?.signal }
    ),

  vectorSearch: (
    tableId: string,
    params: {
      query_text: string
      embedding_model_id?: string
      top_k?: number
      filters?: Record<string, unknown>
    },
    options?: { signal?: AbortSignal }
  ) => {
    const body = {
      registered_table_id: tableId,
      query_text: params.query_text,
      embedding_model_id: params.embedding_model_id,
      top_k: params.top_k || 10,
      filters: params.filters,
    }

    return request<VectorSearchResponse>(`/connectors/registered-tables/${tableId}/vector-search`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal: options?.signal,
    })
  },

  // Access statistics
  getAccessStatistics: (id: string) => projectsApi.getKnowledgeAccessStatistics(id),

  // Access requests (for admins)
  listAccessRequests: (
    id: string,
    params?: { status?: string; page?: number; page_size?: number }
  ) => projectsApi.listKnowledgeAccessRequests(id, params),

  approveAccess: (id: string, requestId: string, action: 'approve' | 'reject') =>
    projectsApi.approveKnowledgeAccess(id, requestId, action),

  revokeAccess: (id: string, requestId: string) => projectsApi.revokeKnowledgeAccess(id, requestId),

  // Storage methods
  introspectStorage: (
    id: string,
    params?: {
      bucket_name?: string
      folder_path?: string
      depth?: StorageIntrospectionDepth
      page?: number
      page_size?: number
      search?: string
    },
    options?: { signal?: AbortSignal }
  ) =>
    request<StorageIntrospectionResponse>(`/connectors/${id}/introspect-storage`, {
      method: 'POST',
      body: JSON.stringify({ connector_id: id, ...params }),
      signal: options?.signal,
    }),

  registerStorageFolders: (
    id: string,
    folders: Array<{ bucket_name: string; folder_path: string }>,
    options?: { project_id?: string; populate_stats?: boolean; signal?: AbortSignal }
  ) =>
    request<RegisterStorageFoldersResponse>(`/connectors/${id}/register-storage-folders`, {
      method: 'POST',
      body: JSON.stringify({
        connector_id: id,
        folders,
        ...(options?.project_id != null ? { project_id: options.project_id } : {}),
        ...(options?.populate_stats != null ? { populate_stats: options.populate_stats } : {}),
      }),
      signal: options?.signal,
    }),

  // FIX: Accept search, order_by, order_desc so the StorageTab can delegate
  // sorting and filtering to the backend instead of doing it client-side.
  listAllRegisteredStorageFolders: (
    params?: {
      project_id?: string
      page?: number
      page_size?: number
      search?: string
      // TODO(split-list-backend): Add `connector_connection_id` and
      // `bucket_name` filters on /api/connectors/all/registered-storage-folders.
      // See frontend/src/components/SplitListExplorer/BACKEND_SPEC.md (#3).
      connector_connection_id?: string
      bucket_name?: string
      /** Filter by storage engine (connection connector type, e.g. azure_blob) */
      connector_type?: string
      /** Filter by creator user id */
      created_by?: string
      omit_linked_for_project?: string
      only_approved_linked_for_project?: string
      include_project_membership_for?: string
      order_by?: string
      order_desc?: boolean
      is_favourite?: boolean
    },
    options?: { signal?: AbortSignal }
  ) => {
    // FIX: DataTable column keys differ from backend field names.
    // Normalize here so the component can pass UI-facing keys directly.
    const orderByFieldMap: Record<string, string> = {
      connector: 'connector_name',
      created: 'created_at',
      name: 'display_name',
    }
    const normalizedParams = params
      ? {
        ...params,
        order_by: params.order_by
          ? (orderByFieldMap[params.order_by] ?? params.order_by)
          : params.order_by,
      }
      : undefined

    const query = buildQueryString(normalizedParams || {})
    return request<{ items: RegisteredStorageFolder[]; total: number }>(
      `/connectors/all/registered-storage-folders${query}`,
      {
        signal: options?.signal,
      }
    )
  },

  listRegisteredStorageFolderFilterFacets: (
    params?: {
      project_id?: string
    },
    options?: { signal?: AbortSignal }
  ) => {
    const query = buildQueryString(params || {})
    return request<{
      connector_types: string[]
      creators: { user_id: string; label: string }[]
    }>(`/connectors/all/registered-storage-folders/filter-facets${query}`, {
      signal: options?.signal,
    })
  },

  listRegisteredStorageFolders: (
    id: string,
    params?: { page?: number; page_size?: number },
    options?: { signal?: AbortSignal }
  ) => {
    const query = buildQueryString(params || {})
    return request<{ items: RegisteredStorageFolder[]; total: number }>(
      `/connectors/${id}/registered-storage-folders${query}`,
      {
        signal: options?.signal,
      }
    )
  },

  unregisterStorageFolder: (folderId: string, options?: { signal?: AbortSignal }) =>
    request<void>(`/connectors/registered-storage-folders/${folderId}`, {
      method: 'DELETE',
      signal: options?.signal,
    }),

  fetchStorageFiles: (
    folderId: string,
    params: {
      page?: number
      page_size?: number
      search?: string
      recursive?: boolean
    },
    options?: { signal?: AbortSignal }
  ) => {
    return request<StorageFilesResponse>(
      `/connectors/registered-storage-folders/${folderId}/files`,
      {
        method: 'POST',
        body: JSON.stringify({
          registered_folder_id: folderId,
          page: params.page || 1,
          page_size: params.page_size || 100,
          search: params.search,
          recursive: params.recursive ?? false,
        }),
        signal: options?.signal,
      }
    )
  },

  syncRegisteredStorageFolder: (folderId: string, options?: { signal?: AbortSignal }) =>
    request<RegisteredStorageFolder>(`/connectors/registered-storage-folders/${folderId}/sync`, {
      method: 'POST',
      signal: options?.signal,
    }),

  // POST /api/connectors/registered-tables/{tableId}/preview-query
  previewQuery: async (
    tableId: string,
    params: {
      query_spec?: Record<string, unknown>
      raw_query?: string
      limit?: number
      additional_registered_table_ids?: string[]
    },
    options?: { signal?: AbortSignal; projectId?: string }
  ) => {
    const projectId = options?.projectId?.trim()
    const raw = await request<Record<string, unknown>>(
      `/connectors/registered-tables/${tableId}/preview-query`,
      {
        method: 'POST',
        body: JSON.stringify(params),
        signal: options?.signal,
        ...(projectId ? { headers: { 'X-Workflow-Project-ID': projectId } } : {}),
      }
    )
    const engine_family = (raw.engine_family ?? raw.engineFamily) as string
    return { ...raw, engine_family } as
      | {
          engine_family: 'sql'
          sql: string
          preview_rows: Record<string, unknown>[]
          cost_estimate: number | null
          index_hints: string[]
        }
      | {
          engine_family: 'nosql'
          rendered_request: Record<string, unknown>
          preview_rows: Record<string, unknown>[]
          diagnostics: string[]
        }
      | {
          engine_family: 'graph'
          rendered_query: string
          nodes: Record<string, unknown>[]
          relationships: Record<string, unknown>[]
          preview_rows: Record<string, unknown>[]
          diagnostics: string[]
        }
      | {
          engine_family: 'prometheus'
          promql: string
          preview_rows: Record<string, unknown>[]
          diagnostics: string[]
        }
  },

  // POST /api/connectors/registered-tables/{tableId}/explain-query-result
  explainQueryResult: (
    tableId: string,
    params: {
      prompt: string
      model_id: string
      nodes: Record<string, unknown>[]
      relationships: Record<string, unknown>[]
    },
    options?: { signal?: AbortSignal }
  ) =>
    request<{ answer: string; estimated_llm_cost_usd: number | null }>(
      `/connectors/registered-tables/${tableId}/explain-query-result`,
      {
        method: 'POST',
        body: JSON.stringify(params),
        signal: options?.signal,
      }
    ),

  // POST /api/connectors/registered-tables/join-preview
  previewDatasetJoin: async (
    params: {
      workflow_output_table_id: string
      ground_truth_table_id: string
      join_sql: string
      limit?: number
      page?: number
      page_size?: number
    },
    options?: { signal?: AbortSignal }
  ) => {
    return request<EvalDatasetJoinPreviewResponse>(
      '/connectors/registered-tables/join-preview',
      {
        method: 'POST',
        body: JSON.stringify(params),
        signal: options?.signal,
      }
    )
  },

  // POST /api/connectors/registered-tables/join-materialize
  materializeDatasetJoin: async (
    params: {
      workflow_output_table_id: string
      ground_truth_table_id: string
      join_sql: string
      table_name: string
      row_limit?: number
      project_id?: string
    },
    options?: { signal?: AbortSignal }
  ) => {
    return request<EvalDatasetJoinMaterializeResponse>(
      '/connectors/registered-tables/join-materialize',
      {
        method: 'POST',
        body: JSON.stringify(params),
        signal: options?.signal,
      }
    )
  },

  previewSessionBenchmarkJoin: async (
    params: {
      agent_id: string
      session_ids: string[]
      session_source: 'agentserve' | 'aba' | 'cwb'
      ground_truth_table_id: string
      join_sql: string
      limit?: number
      page?: number
      page_size?: number
    },
    options?: { signal?: AbortSignal }
  ) => {
    return request<EvalSessionBenchmarkJoinPreviewResponse>(
      '/connectors/eval-datasets/session-benchmark/join-preview',
      {
        method: 'POST',
        body: JSON.stringify(params),
        signal: options?.signal,
      }
    )
  },

  materializeSessionBenchmarkJoin: async (
    params: {
      agent_id: string
      session_ids: string[]
      session_source: 'agentserve' | 'aba' | 'cwb'
      ground_truth_table_id: string
      join_sql: string
      table_name: string
      row_limit?: number
      project_id?: string
    },
    options?: { signal?: AbortSignal }
  ) => {
    return request<EvalDatasetJoinMaterializeResponse>(
      '/connectors/eval-datasets/session-benchmark/join-materialize',
      {
        method: 'POST',
        body: JSON.stringify(params),
        signal: options?.signal,
      }
    )
  },

  downloadStorageFile: async (

    folderId: string,
    filePath: string,
    options?: { signal?: AbortSignal }
  ): Promise<void> => {
    const token = localStorage.getItem(config.auth.tokenKey)
    const response = await connectorsFetch(
      `${config.api.baseUrl}/connectors/registered-storage-folders/${folderId}/download-file`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          registered_folder_id: folderId,
          file_path: filePath,
        }),
        signal: options?.signal,
      }
    )

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}))
      throw new Error(errData?.detail || `Download failed: ${response.status}`)
    }

    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition')
    let filename = filePath.split('/').pop() || 'download'
    if (disposition) {
      const match = disposition.match(/filename="?([^";\n]+)"?/i)
      if (match?.[1]) filename = match[1]
    }

    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  },

  previewStorageFileData: (
    folderId: string,
    filePath: string,
    params?: { page?: number; page_size?: number },
    options?: { signal?: AbortSignal }
  ) => {
    return request<StorageFilePreviewResponse>(
      `/connectors/registered-storage-folders/${folderId}/preview-file`,
      {
        method: 'POST',
        body: JSON.stringify({
          registered_folder_id: folderId,
          file_path: filePath,
          page: params?.page || 1,
          page_size: params?.page_size || 100,
        }),
        signal: options?.signal,
      }
    )
  },
}
